/**
 * API utility functions for making authenticated requests
 */

const API_BASE_URL = '/api/v1';

/**
 * Make an authenticated API call
 * @param {string} endpoint - API endpoint (relative to /api/v1)
 * @param {object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export async function apiCall(endpoint, options = {}) {
  const url = endpoint.startsWith('/') 
    ? `${API_BASE_URL}${endpoint}`
    : `${API_BASE_URL}/${endpoint}`;

  // Get auth token from localStorage
  const authToken = localStorage.getItem('auth_token');
  
  if (!authToken) {
    console.warn('No auth token found in localStorage. User may need to log in.');
  }

  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
  };

  const finalOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include', // Include cookies for auth
    ...(options.body && typeof options.body === 'object' && {
      body: JSON.stringify(options.body)
    }),
  };

  const response = await fetch(url, finalOptions);

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'Network error',
      message: `HTTP ${response.status}: ${response.statusText}`,
    }));
    
    throw new Error(error.error || error.message || 'API request failed');
  }

  return response.json();
}

/**
 * Make a GET request
 */
export function apiGet(endpoint, options = {}) {
  return apiCall(endpoint, {
    ...options,
    method: 'GET',
  });
}

/**
 * Make a POST request
 */
export function apiPost(endpoint, data, options = {}) {
  return apiCall(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Make a PUT request
 */
export function apiPut(endpoint, data, options = {}) {
  return apiCall(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Make a DELETE request
 */
export function apiDelete(endpoint, options = {}) {
  return apiCall(endpoint, {
    ...options,
    method: 'DELETE',
  });
}

// Default export with all methods
const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  call: apiCall
};

export default api;